import React, { useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { useApplicationSettingsContext } from '../../contexts/ApplicationSettingsContext';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';

const statuses = [
  { value: 'created', label: 'Created' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' }
];

interface MobileTabStatusHeaderProps {
  // Tab props
  activeTab: string;
  onTabChange: (tab: string) => void;
  
  // App system props - matching BookingTabs
  isInventoryAppInstalled?: boolean;
  showMaterialsInBookings?: boolean;
  isExpensesAppInstalled?: boolean;
  isPayrollAppInstalled?: boolean;
  showFlatRateInBookings?: boolean;
  /** Show a "Field Quote" tab (directly after Overview) when a field quote is linked. */
  hasFieldQuote?: boolean;
  
  // Status props - now optional since status will be separate
  status?: string;
  createdAt?: string;
  completedAt?: string | null;
  onStatusChange?: (status: string) => void;
  readOnly?: boolean;
}

interface MobileStatusControllerProps {
  status: string;
  createdAt: string;
  completedAt?: string | null;
  onStatusChange?: (status: string) => void;
  readOnly?: boolean;
}

export function MobileStatusController({ 
  status, 
  createdAt, 
  completedAt, 
  onStatusChange, 
  readOnly = false 
}: MobileStatusControllerProps) {
  const currentStatusIndex = statuses.findIndex(s => s.value === status);
  const canStatusGoUp = currentStatusIndex > 0;
  const canStatusGoDown = currentStatusIndex < statuses.length - 1;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'created':
        return 'text-gray-500';
      case 'scheduled':
        return 'text-blue-600';
      case 'in_progress':
        return 'text-yellow-600';
      case 'completed':
        return 'text-green-600';
      default:
        return 'text-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-3 w-3" />;
      case 'cancelled':
        return <XCircle className="h-3 w-3 text-red-400" />;
      default:
        return <Clock className="h-3 w-3" />;
    }
  };

  const handleStatusUp = () => {
    if (canStatusGoUp && onStatusChange && !readOnly) {
      onStatusChange(statuses[currentStatusIndex - 1].value);
    }
  };

  const handleStatusDown = () => {
    if (canStatusGoDown && onStatusChange && !readOnly) {
      onStatusChange(statuses[currentStatusIndex + 1].value);
    }
  };

  if (status === 'cancelled') {
    return (
      <div className="backdrop-blur-xl bg-white/80 border-b border-purple-200/30 px-3 py-2"
           style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
        <div className="flex justify-center">
          <div className="flex items-center space-x-1">
            <XCircle className="h-3 w-3 text-red-500" />
            <span className="text-red-500 text-xs font-bold">Cancelled</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="backdrop-blur-xl bg-white/80 border-b border-gray-200 px-3 py-2"
         style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
      <div className="flex justify-center">
        <div className="flex items-center space-x-1">
          <button
            onClick={handleStatusUp}
            disabled={readOnly || !canStatusGoUp}
            className={`p-0.5 rounded transition-colors ${
              canStatusGoUp && !readOnly
                ? 'text-gray-600 hover:text-gray-700 hover:bg-gray-100/50' 
                : 'text-gray-400 cursor-not-allowed'
            }`}
          >
            <ChevronUp className="h-3 w-3" />
          </button>

          <div className="flex flex-col items-center space-y-0.5">
            <div className={`flex items-center space-x-1 ${getStatusColor(status)}`}>
              {getStatusIcon(status)}
              <span className="text-xs font-bold whitespace-nowrap">
                {statuses[currentStatusIndex]?.label}
              </span>
            </div>
          </div>

          <button
            onClick={handleStatusDown}
            disabled={readOnly || !canStatusGoDown}
            className={`p-0.5 rounded transition-colors ${
              canStatusGoDown && !readOnly
                ? 'text-gray-600 hover:text-gray-700 hover:bg-gray-100/50' 
                : 'text-gray-400 cursor-not-allowed'
            }`}
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MobileTabStatusHeader({ 
  activeTab, 
  onTabChange,
  isInventoryAppInstalled = false,
  showMaterialsInBookings = false,
  isExpensesAppInstalled = false,
  isPayrollAppInstalled = false,
  showFlatRateInBookings = false,
  hasFieldQuote = false,
}: MobileTabStatusHeaderProps) {
  const { disabledPages } = useApplicationSettingsContext();
  const { canAccess } = usePermissionCheck();
  
  const isPnLDisabled = !canAccess.bookingProfitLoss;
  const shouldShowExpensesTab = (isInventoryAppInstalled && showMaterialsInBookings) || isExpensesAppInstalled;
  const isMaterialsDisabled = disabledPages.includes('materials') || !canAccess.bookingMaterials || !shouldShowExpensesTab;
  const isFlatRateDisabled = disabledPages.includes('flat-rates') || !canAccess.bookingRatebook || !isPayrollAppInstalled || !showFlatRateInBookings;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    // Field Quote always renders next after Overview when one is linked.
    ...(hasFieldQuote ? [{ id: 'field-quote', label: 'Field Quote' }] : []),
    ...(isMaterialsDisabled ? [] : [{ id: 'materials', label: 'Expenses' }]),
    ...(isFlatRateDisabled ? [] : [{ id: 'flat-rates', label: 'RateBook' }]),
    ...(isPnLDisabled ? [] : [{ id: 'profit-loss', label: 'Profit & Loss' }])
  ];

  const currentTabIndex = tabs.findIndex(tab => tab.id === activeTab);

  useEffect(() => {
    if (activeTab !== 'overview' && !tabs.some(tab => tab.id === activeTab)) {
      onTabChange('overview');
    }
  }, [activeTab, tabs, onTabChange]);

  const handlePreviousTab = () => {
    const prevIndex = currentTabIndex === 0 ? tabs.length - 1 : currentTabIndex - 1;
    const prevTab = tabs[prevIndex];
    if (prevTab) {
      onTabChange(prevTab.id);
    }
  };

  const handleNextTab = () => {
    const nextIndex = currentTabIndex === tabs.length - 1 ? 0 : currentTabIndex + 1;
    const nextTab = tabs[nextIndex];
    if (nextTab) {
      onTabChange(nextTab.id);
    }
  };

  const showArrows = tabs.length > 1;

  return (
    <div 
      className="backdrop-blur-xl bg-gray-800 border-b border-gray-700 px-3 py-2.5"
      style={{ 
        backdropFilter: 'blur(20px)', 
        WebkitBackdropFilter: 'blur(20px)',
      }}
    >
      <div className="flex-1 relative">
        {/* Dot indicators */}
        {tabs.length > 1 && (
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 flex items-center space-x-1.5 z-10">
            {tabs.map((tab, index) => (
              <div
                key={tab.id}
                className={`rounded-full transition-all duration-200 ${
                  index === currentTabIndex
                    ? 'w-2 h-2 bg-white'
                    : 'w-1 h-1 bg-white/40'
                }`}
              />
            ))}
          </div>
        )}
        
        {/* Tab navigation with arrow buttons */}
        <div className="flex items-center justify-center relative h-10">
          {/* Left arrow */}
          {showArrows && (
            <button
              onClick={handlePreviousTab}
              className="absolute left-0 md:left-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full transition-all text-gray-300 hover:bg-gray-700 active:bg-gray-600 focus:outline-none z-10 active:scale-95"
              style={{ 
                WebkitTapHighlightColor: 'transparent', 
                outline: 'none',
                WebkitAppearance: 'none',
                appearance: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                background: 'transparent',
                border: 'none'
              }}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}

          {/* Current tab label */}
          <h3 className="text-white font-semibold text-lg whitespace-nowrap" style={{ 
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
            letterSpacing: '-0.01em'
          }}>
            {tabs[currentTabIndex]?.label}
          </h3>

          {/* Right arrow */}
          {showArrows && (
            <button
              onClick={handleNextTab}
              className="absolute right-0 md:right-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full transition-all text-gray-300 hover:bg-gray-700 active:bg-gray-600 focus:outline-none z-10 active:scale-95"
              style={{ 
                WebkitTapHighlightColor: 'transparent', 
                outline: 'none',
                WebkitAppearance: 'none',
                appearance: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                background: 'transparent',
                border: 'none'
              }}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
